import type { ProductRecord } from '@shared/models/product.model';
import type {
  Baseline,
  IElement,
  OneOfChildElements,
  OneOfParentElements,
} from '@shared/models/project.model';
import type {
  Recipe,
  RecipeID,
  RecipeRecord,
} from '@shared/models/recipe.model';
import type { ResultsRecord } from '@shared/models/unit.model';
import {
  applyRecipe,
  hostHasRecipeComposition,
  isRecipe,
  removeDeletedProductRefsInRecipe,
} from './recipe.helpers';
import { isElement } from './recursive-element.helpers';
import { guards } from './typeguards.helpers';
import { toLookup } from './utils.helpers';

/**
 * Legacy persist: some baselines stored recipe documents on the JSON blob
 * (`baselines[].recipes`, formerly `buildings[].versions[].recipes`). Not
 * part of {@link Baseline}.
 *
 * After the DEV-3491 recipe-library DB migration this key must be gone —
 * project validation rejects any baseline that still defines it
 * (`VERSION_HAS_EMBEDDED_RECIPES`). Keep this reader only for stamp / export
 * paths that may see pre-migration fixtures in unit tests.
 */
const collectEmbeddedRecipes = (version: Baseline): Recipe[] => {
  const embedded: unknown = Reflect.get(version, 'recipes');
  if (guards.is.array(embedded)) return embedded.filter(isRecipe);
  if (!guards.is.object(embedded)) return [];
  return Object.values(embedded).filter(isRecipe);
};

/**
 * Recipes stored on the version JSON (legacy persist) plus the catalog passed into enrich.
 * Embedded snapshots win when both define the same id, so compute matches the applied recipe.
 */
const collectRecipesForStamp = (
  version: Baseline,
  recipes: Recipe[],
): Recipe[] => {
  const fromVersion = collectEmbeddedRecipes(version);
  if (fromVersion.length === 0) return recipes;
  if (recipes.length === 0) return fromVersion;

  const seen = new Set(fromVersion.map((recipe) => recipe.id));
  return [
    ...fromVersion,
    ...recipes.filter((recipe) => recipe.id && !seen.has(recipe.id)),
  ];
};

/**
 * Recipe lookup for {@link stampMissingRecipeChildrenInVersion}, keyed by id.
 * Build once per version and reuse across proposals — the merge and the lookup are proportional
 * to the recipe catalog, while a healthy project needs no stamping at all.
 *
 * @param recipes - Catalog recipes, and in practice only present on the export paths: the `recipes`
 *   option on {@link enrichProject} is export-only by design, so the enrich that runs while the user
 *   works passes nothing. In the app a host therefore resolves only against recipes embedded on the
 *   version JSON, and one pointing at a catalog-only recipe stays unstamped — tracked in DEV-3666.
 *   Callers that must agree with the app's numbers should omit this argument rather than reach for a
 *   store catalog, or they will stamp hosts the persisted records could not.
 */
export const buildRecipeLookupForStamp = (
  version: Baseline,
  recipes: Recipe[] = [],
): RecipeRecord => toLookup(collectRecipesForStamp(version, recipes));

/**
 * True when a {@link IElement.recipe_id} host has no recipe composition to compute
 * from. Leftover dummy system children (partial project copy) still need a stamp
 * (DEV-3656) — see {@link hostHasRecipeComposition}.
 */
const hostNeedsRecipeChildStamp = (element: IElement): boolean =>
  !!element.recipe_id && !hostHasRecipeComposition(element);

const NO_RECIPE_IDS: ReadonlySet<RecipeID> = new Set();

/**
 * Stamp catalog children onto a host that has {@link IElement.recipe_id} but no
 * recipe composition, then walk nested hosts.
 *
 * @param stampedRecipeIds - Recipe ids already stamped on the current path. A recipe whose
 *   ingredient carries its own `recipe_id` (or a mutual A -> B -> A pair) would otherwise stamp
 *   forever, since every stamped generation reproduces the same unstamped host. Save as keeps an
 *   ingredient's {@link IElement.recipe_id} ({@link createRecipeFromElement}), and the legacy
 *   recipes {@link collectEmbeddedRecipes} reads off the version JSON can nest one too. A stack
 *   overflow inside enrich is a worse failure than an unstamped host. Only ancestors count:
 *   sibling subtrees each start from their own set.
 * @param stampedHostIds - Accumulator; every host a stamp actually ran on is appended.
 */
const stampMissingRecipeChildren = (
  element: IElement,
  path: OneOfParentElements[],
  lookup: RecipeRecord,
  products: ProductRecord,
  stampedRecipeIds: ReadonlySet<RecipeID>,
  stampedHostIds: string[],
): IElement => {
  let current = element;
  let pathRecipeIds = stampedRecipeIds;
  const recipeId = element.recipe_id;
  if (recipeId && !stampedRecipeIds.has(recipeId)) {
    const recipe = hostNeedsRecipeChildStamp(element)
      ? lookup[recipeId]
      : undefined;
    if (isRecipe(recipe)) {
      // Include the host so recipe children inherit its quantities (area, etc.).
      // applyRecipe replaces leftover dummy children; this tree is compute-only.
      current = applyRecipe(
        element,
        removeDeletedProductRefsInRecipe(products, recipe),
        {
          parentPath: [...path, element],
        },
      );
      pathRecipeIds = new Set(stampedRecipeIds).add(recipeId);
      stampedHostIds.push(element.id);
    }
  }

  let childrenChanged = current !== element;
  const childPath = [...path, current];
  const nextChildren: OneOfChildElements[] = current.elements.map(
    (child): OneOfChildElements => {
      if (!isElement(child)) return child;
      const stamped = stampMissingRecipeChildren(
        child,
        childPath,
        lookup,
        products,
        pathRecipeIds,
        stampedHostIds,
      );
      if (stamped !== child) childrenChanged = true;
      return stamped;
    },
  );
  if (!childrenChanged) return current;
  return { ...current, elements: nextChildren };
};

/** Outcome of {@link stampMissingRecipeChildrenInVersion}. */
export type StampedRecipeChildren = {
  /** The input version by reference when nothing needed stamping. */
  version: Baseline;
  /**
   * Hosts a stamp actually ran on. Everything addressed below one of them exists only on
   * {@link StampedRecipeChildren.version} — see {@link omitStampedOnlyResultsRows}.
   */
  stampedHostIds: readonly string[];
};

/**
 * Materialize recipe children on hosts that only store {@link IElement.recipe_id}.
 * Used by proposal result compute so those hosts contribute CO2e (DEV-3656).
 * Does not persist — callers keep the returned tree in memory for compute.
 *
 * Hosts that already have `:r:` children or product descendants are left unchanged.
 *
 * @param lookup - Recipes by id from {@link buildRecipeLookupForStamp}
 */
export const stampMissingRecipeChildrenInVersion = (
  version: Baseline,
  lookup: RecipeRecord,
  products: ProductRecord = {},
): StampedRecipeChildren => {
  const stampedHostIds: string[] = [];
  let changed = false;
  const elements: OneOfChildElements[] = version.elements.map(
    (child): OneOfChildElements => {
      if (!isElement(child)) return child;
      const stamped = stampMissingRecipeChildren(
        child,
        [version],
        lookup,
        products,
        NO_RECIPE_IDS,
        stampedHostIds,
      );
      if (stamped !== child) changed = true;
      return stamped;
    },
  );
  return {
    version: changed ? { ...version, elements } : version,
    stampedHostIds,
  };
};

/**
 * Descendant-id prefixes for stamped hosts. The trailing `:` keeps `host` from matching a
 * sibling id like `host-2`, and building them once avoids a string per (row, host) pair.
 */
const toStampedHostPrefixes = (stampedHostIds: readonly string[]): string[] =>
  stampedHostIds.map((hostId) => `${hostId}:`);

/**
 * Drop {@link ResultsRecord} rows for elements that exist only on the compute-only tree
 * {@link stampMissingRecipeChildrenInVersion} produced.
 *
 * Those children are never persisted, so a row keyed by one of their `:r:` addresses describes an
 * element no stored tree contains. Nothing can read it — the element list, Products tab and report
 * slides all walk the stored tree — while it does grow the document and looks exactly like the
 * leftovers `20260624150000-remap-orphan-recipe-results-records.ts` had to clean up.
 *
 * A row is removed when its id is a descendant of a stamped host (`id` starts with
 * `stampedHostId + ':'`). That covers the whole subtree in one check, including a nested host whose
 * id is already recipe-scoped (`wall:r:recipe:slot:0`): compute enrichment adds its own children
 * below the stamped ones (`${parentId}:p:${index}`, another `:r:` segment), and those ids keep
 * the stamped host as a prefix. Matching the outermost `:r:` parent would attribute those rows to
 * `wall` and leave them in the record when only the nested host was stamped.
 *
 * Host rows are untouched: they do not start with `stampedHostId + ':'`, and their rollup was
 * computed from the stamped children, so removing the children's own rows changes no CO2e.
 * Rows for elements the record builder deliberately writes outside the tree are untouched too,
 * since their id is not under a stamped host.
 *
 * @param stampedHostIds - {@link StampedRecipeChildren.stampedHostIds}; empty for a healthy project
 */
export const omitStampedOnlyResultsRows = (
  record: ResultsRecord,
  stampedHostIds: readonly string[],
): ResultsRecord => {
  if (stampedHostIds.length === 0) return record;

  const prefixes = toStampedHostPrefixes(stampedHostIds);
  const removableIds = Object.keys(record).filter((id) =>
    prefixes.some((prefix) => id.startsWith(prefix)),
  );
  if (removableIds.length === 0) return record;

  const next = { ...record };
  for (const id of removableIds) delete next[id];
  return next;
};
